import { RadioGroup } from "@headlessui/react"
import { classNames } from "@opensea/ui-kit"
import { Check } from "@opensea/ui-kit/icons"
import { usePrivy } from "@privy-io/react-auth"
import React from "react"
import { WALLET_NAME, getWalletLogo } from "@/constants/wallet"
import { Overflow } from "@/design-system/Overflow"
import { Text } from "@/design-system/Text"
import { useVesselActions } from "@/providers/Vessel00/useVesselActions"
import {
  useConnectedWallet,
  useSwitchWallet,
} from "@/providers/Wallet/selectors"
import type { WalletOption } from "./useWalletOptions"
import { useWalletOptions } from "./useWalletOptions"

export function WalletSwitcher() {
  const connectedWallet = useConnectedWallet()
  const { ready, authenticated } = usePrivy()
  const walletOptions = useWalletOptions()
  const switchWallet = useSwitchWallet()
  const { requestLogin, logout, setClickAway } = useVesselActions()

  const onSelectWallet = async (walletName: WALLET_NAME | undefined) => {
    if (!walletName || walletName === connectedWallet) {
      return
    }

    const isOpenSeaWalletLoggedIn = ready && authenticated
    if (!isOpenSeaWalletLoggedIn && walletName === WALLET_NAME.OpenSeaWallet) {
      await logout() // Disconnects the current wallet.
      await requestLogin()
      await setClickAway(true)
      return
    }

    await switchWallet(walletName)
  }

  const renderOption = (item: WalletOption) => {
    const isConnectedWallet = item.value === connectedWallet
    const walletLogoSrc = getWalletLogo(item.value)

    return (
      <RadioGroup.Option
        as="button"
        className={classNames(
          "flex w-full items-center justify-between rounded-medium py-4 pl-10 pr-3 hover:bg-component-gray-1",
          {
            "cursor-default": isConnectedWallet,
            "active:bg-component-gray-2": !isConnectedWallet,
          },
        )}
        disabled={isConnectedWallet}
        key={item.value}
        value={item.value}
      >
        <div className="mr-4 flex items-center gap-x-4 overflow-hidden">
          {walletLogoSrc ? (
            <div className="inline-block shrink-0 align-sub">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img alt="" height={32} src={walletLogoSrc} width={32} />
            </div>
          ) : null}

          <Overflow>
            <Text weight="semibold">{item.label}</Text>
          </Overflow>
        </div>
        {isConnectedWallet ? <Check /> : null}
      </RadioGroup.Option>
    )
  }

  return (
    <RadioGroup
      className="flex flex-col space-y-1"
      onChange={onSelectWallet}
      value={connectedWallet}
    >
      {walletOptions.map(option => renderOption(option))}
    </RadioGroup>
  )
}
